import { useState } from "react";
import {
  Text,
  HStack,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Select,
  Box,
  Checkbox,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  useColorModeValue as mode,
} from "@chakra-ui/react";
import { HexColorPicker } from "react-colorful";

// Components
import Separator from "./separator";

// Utils
import palettes from "./utils/palettes";

// Types
import type { Theme } from "types/carousel";

const themes = [{
  name: "Circles",
  id: "circles",
}, {
  name: "Triangles",
  id: "triangles",
}, {
  name: "Blobs",
  id: "blobs",
}, {
  name: "Dots",
  id: "dots",
}, {
  name: "Blurs 1",
  id: "blurs-1",
}, {
  name: "Blurs 2",
  id: "blurs-2",
}, {
  name: "Blurs 3",
  id: "blurs-3",
}, {
  name: "Blur & Scribble",
  id: "blur-scribble",
}, {
  name: "Scribbles 1",
  id: "scribbles-1",
}, {
  name: "Scribbles 2",
  id: "scribbles-2",
}];
export default function Theme({
  theme,
  onChangeColor,
  onChangeBackground,
}: {
  theme: Theme;
  onChangeColor: (key: string, value: string) => void;
  onChangeBackground: (key: string, value: string | boolean) => void;
}) {
  const [useCustomColors, setUseCustomColors] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState("");
  return (
    <VStack align="start" spacing={4}>
      <VStack spacing={2} align="start">
        <Text as="h3" textStyle="h3.bold.standard">
          Theme
        </Text>
        <Box
          w="30%"
          h="2px"
          bg={mode("text.lightMode.standard", "text.darkMode.standard")}
        />
      </VStack>
      <VStack spacing={4} w="100%">
        <FormControl>
          <HStack justifyContent="space-between" align={"center"} mb={1}>
            <FormLabel htmlFor="backgrounds" m={0}>
              Background Design Elements
            </FormLabel>
            <Switch
              size="sm"
              isChecked={theme.background.active}
              onChange={(e) => onChangeBackground("active", e.target.checked)}
            />
          </HStack>
          <Select
            value={theme.background.type}
            onChange={(e) => onChangeBackground("type", e.target.value)}
          >
            {themes
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((theme) => (
                <option key={theme.id} value={theme.id}>
                  {theme.name}
                </option>
              ))}
          </Select>
        </FormControl>
        <Separator />
        <FormControl>
          <VStack spacing={4} align="left">
            <HStack justifyContent="space-between" alignItems={"center"} mb={1}>
              <FormLabel htmlFor="headshot" m={0}>
                Color Palette
              </FormLabel>
              <Checkbox
                size="sm"
                fontWeight="bold"
                isChecked={useCustomColors}
                onChange={(e) => setUseCustomColors(e.target.checked)}
              >
                Use Custom Colors
              </Checkbox>
            </HStack>
            {useCustomColors ? (
              <HStack spacing={4}>
                <VStack align="left" position={"relative"}>
                  <Text>Primary</Text>
                  <InputGroup size="sm">
                    <InputLeftElement>
                      <Box
                        bg={theme.colors.primary}
                        as="button"
                        w={4}
                        h={4}
                        borderRadius="50%"
                        onClick={() => {
                          setColorPickerOpen((prev) =>
                            prev === "primary" ? "" : "primary"
                          );
                        }}
                      />
                    </InputLeftElement>
                    <Input
                      value={theme.colors.primary}
                      onChange={(e) => onChangeColor("primary", e.target.value)}
                    />
                  </InputGroup>
                  {colorPickerOpen === "primary" && (
                    <Box position={"absolute"} top={"105%"} left={"0"}>
                      <HexColorPicker
                        color={theme.colors.primary}
                        onChange={(color) => onChangeColor("primary", color)}
                      />
                    </Box>
                  )}
                </VStack>
                <VStack align="left" position={"relative"}>
                  <Text>Secondary</Text>
                  <InputGroup size="sm">
                    <InputLeftElement>
                      <Box
                        bg={theme.colors.secondary}
                        as="button"
                        w={4}
                        h={4}
                        borderRadius="50%"
                        onClick={() =>
                          setColorPickerOpen((prev) =>
                            prev === "secondary" ? "" : "secondary"
                          )
                        }
                      />
                    </InputLeftElement>
                    <Input
                      value={theme.colors.secondary}
                      onChange={(e) =>
                        onChangeColor("secondary", e.target.value)
                      }
                    />
                  </InputGroup>
                  {colorPickerOpen === "secondary" && (
                    <Box position={"absolute"} top={"105%"} left={"0"}>
                      <HexColorPicker
                        color={theme.colors.secondary}
                        onChange={(color) => onChangeColor("secondary", color)}
                      />
                    </Box>
                  )}
                </VStack>
                <VStack align="left" position={"relative"}>
                  <Text>Accent</Text>
                  <InputGroup size="sm">
                    <InputLeftElement>
                      <Box
                        bg={theme.colors.accent}
                        as="button"
                        w={4}
                        h={4}
                        borderRadius="50%"
                        onClick={() =>
                          setColorPickerOpen((prev) =>
                            prev === "accent" ? "" : "accent"
                          )
                        }
                      />
                    </InputLeftElement>
                    <Input
                      value={theme.colors.accent}
                      onChange={(e) => onChangeColor("accent", e.target.value)}
                    />
                  </InputGroup>
                  {colorPickerOpen === "accent" && (
                    <Box position={"absolute"} top={"105%"} left={"0"}>
                      <HexColorPicker
                        color={theme.colors.accent}
                        onChange={(color) => onChangeColor("accent", color)}
                      />
                    </Box>
                  )}
                </VStack>
              </HStack>
            ) : (
              <SimpleGrid columns={4} spacing={4}>
                {palettes.map((pallete) => {
                  const isSelected =
                    theme.colors.primary === pallete.primary &&
                    theme.colors.secondary === pallete.secondary &&
                    theme.colors.accent === pallete.accent;
                  return (
                    <Button
                      key={pallete.primary}
                      size="sm"
                      onClick={() => {
                        onChangeColor("primary", pallete.primary);
                        onChangeColor("secondary", pallete.secondary);
                        onChangeColor("accent", pallete.accent);
                      }}
                      borderStyle="solid"
                      borderWidth="2px"
                      borderColor={
                        isSelected
                          ? mode("primary.darkMode.200", "primary.darkMode.500")
                          : "transparent"
                      }
                      display="grid"
                      gridTemplateColumns="repeat(3, 1fr)"
                      gap={0}
                    >
                      <Box bg={pallete.primary} h={4} />
                      <Box bg={pallete.secondary} h={4} />
                      <Box bg={pallete.accent} h={4} />
                    </Button>
                  );
                })}
              </SimpleGrid>
            )}
          </VStack>
        </FormControl>
      </VStack>
    </VStack>
  );
}
